import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Trainings from "./pages/Trainings";
import TrainingPlayer from "./pages/TrainingPlayer";
import Team from "./pages/Team";
import CertificatePreview from "./pages/CertificatePreview";
import Certificates from "./pages/Certificates";
import { isAuthed, loadSession, saveSession } from "./auth";
import { getOnboardingStatus } from "./services/api";
import AppLayout from "./layouts/AppLayout";
import MedicoverOnboardingLanding from "./pages/MedicoverOnboardingLanding";
import { isPlatformAdmin } from "./utils/tenant";
// Admin areas
import AdminUsers from "./pages/AdminUsers";
import AdminEmployeeDetails from "./pages/AdminEmployeeDetails";
import WorksiteDocumentation from "./pages/WorksiteDocumentation";
import PlatformTrainings from "./pages/PlatformTrainings";
import PlatformTrainingEditor from "./pages/PlatformTrainingEditor";
import PlatformRouteDeclarations from "./pages/PlatformRouteDeclarations";
import PlatformRouteDeclarationDetail from "./pages/PlatformRouteDeclarationDetail";
import PlatformDashboard from "./pages/PlatformDashboard";
import Profile from "./pages/Profile";
import Onboarding from "./pages/Onboarding";
import AdminAssignments from "./pages/AdminAssignments";
import AdminManagerAssignments from "./pages/AdminManagerAssignments";
import Landing from "./pages/Landing";
import KycRegister from "./pages/KycRegister";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
// Platform-admin operations console: standalone UI, deliberately outside AppLayout
import ConsoleGate from "./pages/console/ConsoleGate";


function AuthedRoute({ children }) {
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loadedOnboarding, setLoadedOnboarding] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  function hasOnboardingWork(onb) {
    if (!onb) return false;
    if (onb.is_complete === false) return true;
    if (Number(onb.blocking_tasks_count || 0) > 0) return true;
    if (Array.isArray(onb.tasks)) {
      return onb.tasks.some((t) => {
        const s = (t?.status || "").toString().toLowerCase();
        return s === "pending" || s === "in_progress" || s === "expired";
      });
    }
    return false;
  }

  useEffect(() => {
    let alive = true;
    if (!isAuthed()) {
      setChecking(false);
      return () => {
        alive = false;
      };
    }

    (async () => {
      setChecking(true);
      setError(null);

      // Try to reuse cached onboarding in session first
      try {
        const sess = loadSession();
        const rawSessOnb = sess?.onboarding;
        const sessOnb = rawSessOnb?.onboarding ?? rawSessOnb;
        if (sessOnb) {
          if (!alive) return;
          setOnboarding(sessOnb);
          setLoadedOnboarding(true);
          setChecking(false);
          return;
        }
      } catch (e) {
        // ignore session read errors and continue to fetch
      }

      // Fetch onboarding status once
      try {
        const data = await getOnboardingStatus();
        if (!alive) return;
        setOnboarding(data);
        setLoadedOnboarding(true);
        try {
          const s = loadSession() || {};
          s.onboarding = data;
          saveSession(s);
        } catch (e) {
          // ignore persistence errors
        }
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "Failed to load onboarding status");
      } finally {
        if (alive) setChecking(false);
      }
    })();

    return () => {
      alive = false;
    };
    // run on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // React to route changes and onboarding state without re-fetching
  useEffect(() => {
    if (!isAuthed()) return;
    if (!loadedOnboarding) return; // wait until onboarding is known

    const currentUser = loadSession()?.user;
    const isPlat = isPlatformAdmin(currentUser);
    const hasWork = hasOnboardingWork(onboarding);

    if (hasWork) {
      if (location.pathname !== "/onboarding") {
        navigate("/onboarding", { replace: true });
      }
      return;
    }

    // Onboarding complete — don't stay on /onboarding
    if (onboarding && onboarding.is_complete && location.pathname === "/onboarding") {
      navigate("/dashboard", { replace: true });
    }

    if (isPlat && location.pathname === "/onboarding") {
      navigate("/dashboard", { replace: true });
    }
  }, [loadedOnboarding, onboarding, location.pathname, navigate]);

  if (!isAuthed()) return <Navigate to="/home" replace />;

  const hideNav = checking || hasOnboardingWork(onboarding);

  // while checking or on error render the AppLayout with an override so Topbar (logout) stays available
  if (checking || error) {
    const override = checking ? (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold">Se încarcă…</h1>
        <div className="text-sm text-slate-500 mt-3">Se verifică starea de onboarding...</div>
      </div>
    ) : (
      <div className="max-w-3xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold">Eroare</h1>
        <div className="text-sm text-red-600 mt-3">{error}</div>
      </div>
    );

    if (React.isValidElement(children)) {
      return React.cloneElement(children, { hideNav, childrenOverride: override });
    }
    return children;
  }

  if (React.isValidElement(children)) {
    return React.cloneElement(children, { hideNav });
  }
  return children;
}

function ManagerRoute({ children }) {
  const user = loadSession()?.user;
  return user?.derived?.isManager ? (
    children
  ) : (
    <Navigate to="/dashboard" replace />
  );
}

function OrgAdminRoute({ children }) {
  const user = loadSession()?.user;
  const isOrgAdmin =
    user?.derived?.isOrgAdmin ||
    (Array.isArray(user?.roles) && user.roles.includes("org_super_admin"));
  return isOrgAdmin ? children : <Navigate to="/dashboard" replace />;
}

function PlatformAdminRoute({ children }) {
  const user = loadSession()?.user;
  const isPlatformAdmin =
    user?.derived?.isPlatformAdmin ||
    (Array.isArray(user?.roles) && user.roles.includes("platform_admin"));
  return isPlatformAdmin ? children : <Navigate to="/dashboard" replace />;
}

function PublicRoute({ children }) {
  // Re-read auth on every render so post-logout navigation works correctly
  return isAuthed() ? <Navigate to="/dashboard" replace /> : children;
}

export default function App() {

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/home" element={<Landing />} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/kyc-register" element={<KycRegister />} />

        {/* Operations console — unguessable path, platform_admin enforced by the API */}
        <Route path="/_ops/:key" element={<ConsoleGate />} />

        <Route
          path="/"
          element={
            <AuthedRoute>
              <AppLayout />
            </AuthedRoute>
          }
        >
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="medicover-onboarding" element={<MedicoverOnboardingLanding />} />
          <Route path="onboarding" element={<Onboarding />} />
          <Route path="trainings" element={<Trainings />} />
          <Route path="certificates" element={<Certificates />} />
          <Route path="training/:id" element={<TrainingPlayer />} />
          <Route path="profile" element={<Profile />} />
          <Route
            path="team"
            element={
              <ManagerRoute>
                <Team />
              </ManagerRoute>
            }
          />

          {/* Org Admin */}
          <Route
            path="admin/users"
            element={
              <OrgAdminRoute>
                <AdminUsers />
              </OrgAdminRoute>
            }
          />

          {/* Org Admin - Employee Details */}
          <Route
            path="admin/users/:id"
            element={
              <OrgAdminRoute>
                <AdminEmployeeDetails />
              </OrgAdminRoute>
            }
          />

          {/* Org Admin */}
          <Route
            path="admin/assignments"
            element={
              <OrgAdminRoute>
                <AdminAssignments />
              </OrgAdminRoute>
            }
          />

          <Route
            path="admin/manager-assignments"
            element={
              <OrgAdminRoute>
                <AdminManagerAssignments />
              </OrgAdminRoute>
            }
          />

          <Route
            path="admin/worksite-docs"
            element={
              <OrgAdminRoute>
                <WorksiteDocumentation />
              </OrgAdminRoute>
            }
          />

          {/* Platform Admin */}
          <Route
            path="platform/dashboard"
            element={
              <PlatformAdminRoute>
                <PlatformDashboard />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="platform/trainings"
            element={
              <PlatformAdminRoute>
                <PlatformTrainings />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="platform/trainings/:id"
            element={
              <PlatformAdminRoute>
                <PlatformTrainingEditor />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="platform/users/:id"
            element={
              <PlatformAdminRoute>
                <AdminEmployeeDetails />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="platform/route-declarations"
            element={
              <PlatformAdminRoute>
                <PlatformRouteDeclarations />
              </PlatformAdminRoute>
            }
          />
          <Route
            path="platform/route-declarations/:id"
            element={
              <PlatformAdminRoute>
                <PlatformRouteDeclarationDetail />
              </PlatformAdminRoute>
            }
          />

          <Route
            path="certificate/:id/preview"
            element={<CertificatePreview />}
          />
          <Route index element={<Navigate to="dashboard" replace />} />
        </Route>

        <Route
          path="*"
          element={
            <Navigate to={isAuthed() ? "/dashboard" : "/login"} replace />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
